// @ts-nocheck
import { DRAFT_FINAL_HANDOFF_PARAM, parseDraftFinalHandoffId } from '../../../utils/draftFinal';
import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useUser, useAuth } from '@clerk/clerk-react';
import { useOrganization } from '../../../contexts/OrganizationContext';
import { useOrganizationUsage } from '../../../contexts/OrganizationUsageContext';
import { useNotification } from '../../../hooks/useNotification';
import type { DraftFinalRequest } from '../../../contexts/WidgetContext';
import { useWorkflowEditor } from '../../../hooks/useWorkflowEditor';
import { useTokenBalancePolling } from '../../../hooks/useTokenBalancePolling';
import { useLocalization, type Language } from '../../../config/localization';
import { useWidgetContext } from '../../../contexts/WidgetContext';
import { useSidebar } from '../../../contexts/SidebarContext';
import { Icon, ICONS } from '../../ui/icons';
import { saveWorkflowToHistory } from '../../../utils/workflowHistory';
import { estimateWorkflowCost, MEDIA_DURATION_FORMULA_PATTERNS } from '../../../utils/workflowCostEstimator';
import { isRecreateBlobForOrg } from '../../../utils/recreateStorage';
import { resolveCategoryForContext } from '../../../utils/sectionCategory';
import { formatModelSlotGenerationError } from '../../../utils/generationErrorFormatter';
import { useGenerationDispatch } from './useGenerationDispatch';
// import StyleSelectorWidget from '../../widgets/StyleSelectorWidget'; // Removed
// import ImageUploadWidget from '../../widgets/ImageUploadWidget'; // Removed
import PromptEditorWidget from '../../widgets/PromptEditorWidget';
import PromptEnhancerWidget from '../../widgets/PromptEnhancerWidget';
import CollectionsSelectorWidget from '../../widgets/CollectionsSelectorWidget';
import InputTextSmallWidget from '../../widgets/InputTextSmallWidget';
import SlotsInputMediaSmallWidget from '../../widgets/SlotsInputMediaSmallWidget';
// import UpscaleSettingsAuraSrWidget, { type AuraSrCheckpoint } from '../../widgets/UpscaleSettingsAuraSrWidget'; // Removed
// import ClarityUpscalerWidget from '../../widgets/ClarityUpscalerWidget'; // Removed
import DynamicWidgetRenderer from '../../widgets/DynamicWidgetRenderer';
import EmbeddedDynamicWidgetRenderer from '../../widgets/EmbeddedDynamicWidgetRenderer';
import Tooltip from '../../ui/Tooltip';
import DataPopup from '../../ui/overlays/modals/DataPopup';
import NotificationModal from '../../ui/overlays/modals/NotificationModal';
import TokenBalanceCard from '../../ui/displays/TokenBalanceCard';
import {
  advanceWidgetSeeds,
  captureWidgetSeedAdvances,
  collectWidgetPayloads,
  getActionButtonConfig,
  categorizeFilesForPreview,
} from '../../../utils/widgetPayloadCollector';
import {
  categorizeGalleryByMediaType,
  detectMediaTypeFromNameAndMime,
} from '../../../utils/mediaTypeDetection';
import { getSentRequestHistory } from '../../../utils/requestHistory';
import { buildAdminHistoryLoader, resolveAdminHistoryWorkflow } from '../../../utils/adminRequestHistory';
import { calculateExpectedSlots } from '../../../utils/requestCalculator';
import {
  prepareFanOutInputs,
  refreshGenerationToken,
  uploadGenerationInputs,
} from '../../../utils/generationPreUpload';
import type { IWorkflowWidget } from '../../../interfaces/IWorkflowWidget';
import { extractTargetDimensions } from '@functions/utils/target-dimensions';
import { addSentryBreadcrumb } from '../../../utils/sentry';
import { getMultipleImageDimensions, type ImageDimensions } from '@functions/utils/image-dimensions';

interface WorkflowWidgetType {
  id: number;
  name: string;
  description?: string;
  icon?: string;
  is_active: boolean;
  is_dynamic: boolean;
  display_type: 'small' | 'big';
}

interface WorkflowWidget {
  id: string;
  workflow_id: string;
  widget_type_id: number;
  is_enabled: boolean;
  display_order: number;
  config_data?: string;
  widget_type: WorkflowWidgetType;
}

interface WorkflowWithTranslations {
  id: number;
  visual_id?: string;
  title: string;
  description_key: string;
  category: string;
  is_available_for_all: boolean;
  default_prompt_id: string | null;
  created_by: string;
  created_at: string;
  updated_at?: string;
  description_translations: Record<string, string>;
  action_button_type?: 'generate' | 'upscale' | 'process' | 'create';
  input_files_logic?: 'single_per_request' | 'all_per_request';
  pricing_formula?: string;
  min_image_width?: number | null;
  min_image_height?: number | null;
  max_image_width?: number | null;
  max_image_height?: number | null;
  settings?: {
    auto_prompt_enhance?: boolean;
    enhance_field_mappings?: Array<{ source: string; target: string }>;
    steps?: Array<{
      name: string;
      output_mappings: Array<{ source: string; target: string }>;
    }>;
  } | null;
}

// Embedded dynamic widget field
interface EmbeddedWidgetField {
  field_id: string;
  field_type: string;
  label: string;
  tooltip?: string | null;
  display_order: number;
  config_data: Record<string, any>;
}

// Embedded dynamic widget config
interface EmbeddedDynamicWidgetConfig {
  name: string;
  description?: string | null;
  display_order: number;
  fields: EmbeddedWidgetField[];
}

// Union type for widgets in sidebar (regular or embedded)
interface EmbeddedWidgetItem {
  id: string;
  display_order: number;
  isEmbedded: true;
  embeddedConfig: EmbeddedDynamicWidgetConfig;
}

type SidebarWidgetItem = WorkflowWidget | EmbeddedWidgetItem;

interface OrganizationWorkflowWithWidgets {
  id: string;
  organization_id: string;
  workflow_id: number;
  custom_prompt_id?: string;
  is_enabled: boolean;
  assigned_by: string;
  assigned_at: string;
  updated_at?: string;
  workflow: WorkflowWithTranslations;
  widgets: WorkflowWidget[];
  dynamic_widget?: EmbeddedDynamicWidgetConfig | null;
  source?: 'global' | 'organization';
}

interface ImageGenerationContextProps {
  selectedLanguage: Language;
  onOpenTileSelector?: (workflows: any[], selectedId: string | null, onSelect: (id: string) => void) => void;
}

export default function ImageGenerationContext({
  selectedLanguage,
  onOpenTileSelector
}: ImageGenerationContextProps) {
  const { user } = useUser();
  const { getToken } = useAuth();
  const { currentOrganization, userAccess } = useOrganization();
  const { refreshUsage } = useOrganizationUsage();
  const { activeContext } = useSidebar();
  const t = useLocalization(selectedLanguage);
  const { widgetState, updateWidgetState, addGenerationSlot, updateGenerationSlot, updateDynamicWidgetState, getDynamicWidgetState, draftFinalRequest, clearDraftFinalRequest, registerDraftFinalConsumer, startDraftFinalHandoff } = useWidgetContext();
  const { notification, hideNotification, showError, showSuccess } = useNotification();

  // Progressive polling for token balance updates (only in generation sections)
  const shouldPollBalance = activeContext === 'image' || activeContext === 'video' || activeContext === 'audio' || activeContext === 'text' || activeContext === '3d';
  const { isRefreshing: isRefreshingBalance } = useTokenBalancePolling(shouldPollBalance);

  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  // Force re-render to update token cost when widgets change
  const [renderTrigger, setRenderTrigger] = useState(0);

  // Store input image dimensions for formula calculation
  const [inputImageDimensionsBySource, setInputImageDimensionsBySource] = useState<{
    slots: ImageDimensions[];
    standard: ImageDimensions[];
  }>({ slots: [], standard: [] });
  const inputImageDimensions = [
    ...inputImageDimensionsBySource.slots,
    ...inputImageDimensionsBySource.standard,
  ];

  // Lightweight workflow list for dropdown
  const [availableWorkflows, setAvailableWorkflows] = useState<any[]>([]);
  const [selectedWorkflow, setSelectedWorkflow] = useState<OrganizationWorkflowWithWidgets | null>(null);
  // Cache for full workflow details
  const [workflowDetailsCache, setWorkflowDetailsCache] = useState<Map<string, OrganizationWorkflowWithWidgets>>(new Map());
  const [loading, setLoading] = useState(false);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [workflowAccessError, setWorkflowAccessError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generationProgress, setGenerationProgress] = useState({ current: 0, total: 0 });
  const [isQueueing, setIsQueueing] = useState(false);
  const [queuedCount, setQueuedCount] = useState(0);
  const [showPayloadPreview, setShowPayloadPreview] = useState(false);
  const [validationErrorMessage, setValidationErrorMessage] = useState<string | null>(null);
  // True when recreate explicitly set collections (even to []), prevents widget auto-selecting default
  const [collectionsExplicitlySet, setCollectionsExplicitlySet] = useState(false);
  // Ref mirrors state for sync access in effects (avoids stale closures)
  const collectionsExplicitlySetRef = useRef(false);
  // Track previous org ID to distinguish initial async load from genuine org switch
  const prevOrgIdRef = useRef<number | undefined>(undefined);
  // Load selected collections from localStorage with organization-specific key
  const getCollectionsStorageKey = () => `selectedCollections_${currentOrganization?.id || 'global'}`;

  // Load last selected workflow from localStorage with organization-specific and context-specific key
  const getWorkflowStorageKey = () => `lastWorkflow_${activeContext}_${currentOrganization?.id || 'global'}`;

  const [selectedCollections, setSelectedCollections] = useState<number[]>(() => {
    if (typeof window !== 'undefined' && currentOrganization?.id) {
      try {
        const stored = localStorage.getItem(getCollectionsStorageKey());
        return stored ? JSON.parse(stored) : [];
      } catch (error) {
        console.warn('Failed to load selected collections from localStorage:', error);
        return [];
      }
    }
    return [];
  });

  // Limit exceeded modal state
  const [limitExceededModal, setLimitExceededModal] = useState<{
    isOpen: boolean;
    type: 'user' | 'organization' | null;
  }>({ isOpen: false, type: null });

  // Edit workflow modal hook (for global admins)
  const workflowEditor = useWorkflowEditor({
    onSaveSuccess: async (workflowId) => {
      // Invalidate cache so fresh data is fetched
      setWorkflowDetailsCache(prev => {
        const newCache = new Map(prev);
        newCache.delete(workflowId.toString());
        return newCache;
      });
      // Refetch complete workflow details
      await loadWorkflowDetails(workflowId.toString());
    }
  });


  // Enhanced collections setter with localStorage persistence
  const handleCollectionsChange = useCallback((newCollections: number[]) => {
    setSelectedCollections(newCollections);
    // User made an explicit choice — clear the recreate guard so future loads can auto-select defaults
    setCollectionsExplicitlySet(false);
    collectionsExplicitlySetRef.current = false;
    if (typeof window !== 'undefined' && currentOrganization?.id) {
      try {
        localStorage.setItem(getCollectionsStorageKey(), JSON.stringify(newCollections));
      } catch (error) {
        console.warn('Failed to save selected collections to localStorage:', error);
      }
    }
  }, [currentOrganization?.id]);

  // Load collections from localStorage when organization changes
  useEffect(() => {
    if (currentOrganization?.id) {
      const isGenuineOrgSwitch = prevOrgIdRef.current !== undefined && prevOrgIdRef.current !== currentOrganization.id;
      prevOrgIdRef.current = currentOrganization.id;

      if (isGenuineOrgSwitch) {
        // User switched orgs — clear recreate guard
        setCollectionsExplicitlySet(false);
        collectionsExplicitlySetRef.current = false;
      }

      if (collectionsExplicitlySetRef.current) {
        // Recreate collections are still active — don't overwrite with localStorage
        return;
      }

      // Normal flow: load from localStorage (initial load or org switch)
      setCollectionsExplicitlySet(false);
      try {
        const stored = localStorage.getItem(getCollectionsStorageKey());
        if (stored) {
          const parsedCollections = JSON.parse(stored);
          setSelectedCollections(parsedCollections);
        } else {
          // No stored selection for this org — clear stale IDs from previous org
          setSelectedCollections([]);
        }
      } catch (error) {
        console.warn('Failed to load collections from localStorage:', error);
        setSelectedCollections([]);
      }
    }
  }, [currentOrganization?.id]);

  // Widget refs for dynamic payload collection
  const widgetRefs = useRef<{ [key: string]: React.RefObject<IWorkflowWidget> }>({});

  // Discount info for tooltip display
  const discountInfoRef = useRef<{ fullPrice: number } | null>(null);

  // Helper function to get or create widget ref
  const getWidgetRef = (widgetId: string): React.RefObject<IWorkflowWidget> => {
    if (!widgetRefs.current[widgetId]) {
      widgetRefs.current[widgetId] = React.createRef<IWorkflowWidget>();
    }
    return widgetRefs.current[widgetId];
  };

  // Token cost calculation result
  interface TokenCostResult {
    totalCost: number;
    minCost?: number;           // only for multi-file
    maxCost?: number;           // only for multi-file
    requestCount: number;
    perRequestCost?: number;    // for multi-generation (num_images > 1)
    perRequestCosts?: number[]; // for multi-file
    isMultiGeneration?: boolean; // distinguish from multi-file
  }

  // Calculate token cost for current workflow
  // NOTE: This is NOT memoized - it runs on every render to get fresh widget values
  // Returns: TokenCostResult (cost details), -1 (loading), -2 (requires images), -3 (missing params), -4 (requires audio), -5 (requires video), -6 (requires audio or video), -7 (requires prompt), -8 (requires text), -9 (requires any of _may_zero inputs), null (error)
  const calculateTokenCost = (): TokenCostResult | number | null => {
    discountInfoRef.current = null;
    if (!selectedWorkflow?.workflow?.pricing_formula) {
      return 100; // Default cost
    }

    try {
      // Collect current widget values using the same mechanism as generation
      if (!widgetRefs.current) {
        return -1;
      }

      const activeWidgetRefs = Object.values(widgetRefs.current).filter(ref => ref.current);

      // If widgets haven't loaded yet, return loading state
      if (activeWidgetRefs.length === 0 || loadingDetails) {
        return -1; // Loading state
      }

      const payloadResult = collectWidgetPayloads(
        activeWidgetRefs,
        selectedLanguage,
        selectedWorkflow.workflow.input_files_logic || 'all_per_request'
      );
      // Session media can expose already-measured partial aggregates while a
      // sibling probe is pending or failed. Never display a cost based on that
      // incomplete money-path payload; generation validation fails closed too.
      if (payloadResult.hasPricingErrors) {
        return -1;
      }
      const widgetPayload = payloadResult.payload; // Note: it's 'payload', not 'finalPayload'
      const hasInputImages = payloadResult.galleryUrls.some(item => item.mediaType === 'image')
        || payloadResult.requiredFiles.some(file =>
          detectMediaTypeFromNameAndMime(file.name, file.type) === 'image'
        );

      // Check if formula is a simple constant (no variables)
      const isSimpleConstant = /^\d+(\.\d+)?$/.test(selectedWorkflow.workflow.pricing_formula.trim());

      // If payload is empty and formula requires variables, return loading state
      // But if formula is a simple constant, we can evaluate it immediately
      if ((!widgetPayload || Object.keys(widgetPayload).length === 0) && !isSimpleConstant) {
        return -1; // Loading state
      }

      // Build context from widget payload (these are the actual values that will be sent to API)
      const context: Record<string, any> = {
        // Standard parameters
        num_images: widgetPayload?.gen_count || widgetPayload?.num_images || widgetState.numImages || 1,
        guidance_scale: widgetPayload?.guidance_scale || widgetState.guidanceScale || 7.5,
        safety_tolerance: widgetPayload?.safety_tolerance || widgetState.safetyTolerance || 5,
        num_inference_steps: widgetPayload?.num_inference_steps || widgetState.numInferenceSteps || 50,

        // Include all widget payload values for formula evaluation
        ...(widgetPayload || {})
      };

      // Extract target image dimensions from widget payload via shared utility.
      // Passing dynamic_widget scopes the per-field `_width/_height` fallback to
      // image_size widget fields only (prevents picking up input-media widgets'
      // `video_width`/`video_height`).
      const targetImageDimensions = extractTargetDimensions(
        widgetPayload,
        selectedWorkflow.dynamic_widget
      );

      // Delegate formula requirement checks + evaluation + margin math to the
      // pure estimator (shared with the Multi section). Side effect (discount
      // info ref) stays here so the estimator itself is DOM/ref-free.
      const { result, discountInfo } = estimateWorkflowCost(
        selectedWorkflow.workflow,
        context,
        {
          inputImageDimensions,
          slotsInputImageDimensions: inputImageDimensionsBySource.slots,
          standardInputImageDimensions: inputImageDimensionsBySource.standard,
          hasInputImages,
          targetImageDimensions,
          marginDiscount: currentOrganization?.margin_discount || 0,
          dynamicWidget: selectedWorkflow.dynamic_widget
        }
      );

      discountInfoRef.current = discountInfo;
      return result;
    } catch (error: any) {
      // Check if error is due to missing parameters (widgets not loaded yet)
      if (error.message?.includes('contains identifier')) {
        return -3; // Missing parameters - widgets still loading
      }

      console.error('❌ Error calculating token cost:', {
        workflow: selectedWorkflow.workflow.title,
        formula: selectedWorkflow.workflow.pricing_formula,
        error: error.message
      });
      return null; // Error: formula evaluation failed
    }
  };

  // Load available workflows when component becomes visible, organization or context changes
  useEffect(() => {
    if (currentOrganization && user) {
      // Clear selected workflow and cache when organization or context changes
      // so the fallback selection picks the first workflow from the new list
      setSelectedWorkflow(null);
      setWorkflowDetailsCache(new Map());
      loadAvailableWorkflows();
    } else {
      setAvailableWorkflows([]);
      setSelectedWorkflow(null);
      setWorkflowDetailsCache(new Map());
    }
  }, [currentOrganization?.id, user, activeContext]);

  // Trigger re-render periodically but only when widgets are loaded and ready
  useEffect(() => {
    if (!selectedWorkflow) return;

    // Start with faster polling to catch widget loading
    let pollCount = 0;
    const maxFastPolls = 20; // Poll fast for 4 seconds (20 * 200ms)
    let slowIntervalId: ReturnType<typeof setInterval> | null = null;

    const interval = setInterval(() => {
      pollCount++;
      setRenderTrigger(prev => prev + 1);

      // After initial fast polling, switch to slower updates
      // Guard: only transition once (if event loop stalls, multiple callbacks may fire)
      if (pollCount > maxFastPolls && slowIntervalId === null) {
        clearInterval(interval);
        // Start slower polling for widget value changes
        slowIntervalId = setInterval(() => {
          setRenderTrigger(prev => prev + 1);
        }, 500); // 500ms for slider updates
      }
    }, 200); // Fast polling initially

    return () => {
      clearInterval(interval);
      if (slowIntervalId !== null) {
        clearInterval(slowIntervalId);
      }
    };
  }, [selectedWorkflow?.id]);

  // Extract image dimensions from uploaded files and gallery items for formula calculation
  useEffect(() => {
    let cancelled = false;

    const extractDimensions = async () => {
      if (!widgetRefs.current) return;

      const activeWidgetRefs = Object.values(widgetRefs.current).filter(ref => ref.current);
      if (activeWidgetRefs.length === 0) {
        setInputImageDimensionsBySource({ slots: [], standard: [] });
        return;
      }

      const payloadResult = collectWidgetPayloads(
        activeWidgetRefs,
        selectedLanguage,
        selectedWorkflow?.workflow?.input_files_logic || 'all_per_request'
      );
      try {
        const [measuredSlots, measuredStandard] = await Promise.all([
          payloadResult.unmeasuredSlotsImageFiles.length > 0
            ? getMultipleImageDimensions(payloadResult.unmeasuredSlotsImageFiles)
            : Promise.resolve([]),
          payloadResult.unmeasuredStandardImageFiles.length > 0
            ? getMultipleImageDimensions(payloadResult.unmeasuredStandardImageFiles)
            : Promise.resolve([]),
        ]);
        if (!cancelled) {
          setInputImageDimensionsBySource({
            slots: [...payloadResult.slotsInputImageDimensions, ...measuredSlots],
            standard: [...payloadResult.standardInputImageDimensions, ...measuredStandard],
          });
        }
      } catch (error) {
        console.error('Failed to extract image dimensions:', error);
        if (!cancelled) {
          setInputImageDimensionsBySource({
            slots: payloadResult.slotsInputImageDimensions,
            standard: payloadResult.standardInputImageDimensions,
          });
        }
      }
    };

    extractDimensions();
    return () => { cancelled = true; };
  }, [renderTrigger, selectedLanguage]); // Re-extract when widget values or validation language changes

  // Listen for URL parameter changes and update selected workflow
  useEffect(() => {
    const workflowIdFromUrl = searchParams.get('workflow');
    if (workflowIdFromUrl && availableWorkflows.length > 0) {
      // Check if we need to load this workflow
      // Compare with workflow_id (numeric) not visual_id
      if (!selectedWorkflow || selectedWorkflow.workflow_id?.toString() !== workflowIdFromUrl) {
        // Don't set selectedWorkflow with lightweight data - instead load full details
        loadWorkflowDetails(workflowIdFromUrl);
      }
    }
  }, [searchParams, availableWorkflows]);

  // Handle recreate from gallery - restore basic settings from sessionStorage
  // Note: Dynamic widget values (aspect_ratio, etc.) are written to localStorage
  // in GalleryImageCard BEFORE navigation to ensure EmbeddedDynamicWidgetRenderer
  // reads them on mount. Media URLs are also stored in sessionStorage by GalleryImageCard.
  useEffect(() => {
    const isRecreate = searchParams.get('recreate') === 'true';

    // Wait for the organization to resolve before consuming anything.
    //
    // This component is mounted by `ContextPanel` as soon as Clerk reports a
    // user, i.e. while `checkUserAccess` is still in flight and
    // `currentOrganization` is still null. The stamp check below fails closed on
    // an unknown organization, so running now would reject a perfectly
    // legitimate SAME-org hand-off, delete both blobs and strip `recreate` from
    // the URL — with the organization in the dep list this effect simply runs
    // again once the id lands. Reachable on any cold load of
    // `/image?recreate=true`: F5 mid-recreate, or the automatic
    // `lazyWithRetry`/`ChunkErrorBoundary` reload after a deploy.
    if (isRecreate && !currentOrganization?.id) {
      return;
    }

    if (isRecreate) {
      // Read and clear recreate settings from sessionStorage
      const settingsJson = sessionStorage.getItem('recreate-settings');
      sessionStorage.removeItem('recreate-settings'); // Clear immediately

      if (settingsJson) {
        try {
          const settings = JSON.parse(settingsJson);

          // Check timestamp - settings older than 30 seconds are invalid
          if (Date.now() - settings.timestamp > 30000) {
            console.warn('Recreate settings expired');
          } else if (!isRecreateBlobForOrg(settings, currentOrganization?.id)) {
            // Captured in another organization (switch between Recreate and this
            // effect) — the prompt and raw settings belong to that tenant, so
            // they must not seed a generation billed here. Fails closed on an
            // unstamped blob; see `src/utils/recreateStorage.ts`.
            console.warn('Recreate settings belong to another organization');
          } else if (settings.rawInput) {
            const input = settings.rawInput;

            // Update basic widget state (prompt, numImages, etc.)
            // Dynamic widget values are already in localStorage (written by GalleryImageCard)
            updateWidgetState({
              prompt: input.prompt || input.text || '',
              numImages: input.num_images || input.gen_count || 1,
              guidanceScale: input.guidance_scale || 7.0,
              safetyTolerance: input.safety_tolerance?.toString() || '2',
              numInferenceSteps: input.num_inference_steps || 30
            });
          }
        } catch (error) {
          console.error('Failed to parse recreate settings:', error);
        }
      }

      // Restore collections from recreate (empty array = clear selections)
      const collectionsJson = sessionStorage.getItem('recreate-collections');
      sessionStorage.removeItem('recreate-collections');
      if (collectionsJson) {
        try {
          const parsed = JSON.parse(collectionsJson);
          // Support timestamped format { collectionIds, timestamp } with 30s TTL
          let collectionIds: unknown;
          if (parsed && typeof parsed === 'object' && 'timestamp' in parsed) {
            if (Date.now() - parsed.timestamp > 30000) {
              console.warn('Recreate collections expired');
              collectionIds = null;
            } else if (!isRecreateBlobForOrg(parsed, currentOrganization?.id)) {
              // Collection ids are per-organization — applying another tenant's
              // ids here would file the generation into whatever those ids
              // happen to mean in this organization.
              console.warn('Recreate collections belong to another organization');
              collectionIds = null;
            } else {
              collectionIds = parsed.collectionIds;
            }
          } else {
            // Legacy bare array format — no timestamp and no organization stamp,
            // so it can only be a cross-deploy leftover from an earlier session
            // in this tab. Rejected for the same reason as an unstamped blob:
            // its ids may belong to another tenant. Costs one re-click.
            console.warn('Recreate collections in legacy format — ignored');
            collectionIds = null;
          }
          if (Array.isArray(collectionIds)) {
            const validIds = collectionIds.filter((id: unknown) => typeof id === 'number');
            setSelectedCollections(validIds);
            setCollectionsExplicitlySet(true);
            collectionsExplicitlySetRef.current = true;
          }
        } catch (e) {
          console.warn('Failed to restore recreate collections:', e);
        }
      }

      // Remove recreate param from URL to prevent re-triggering
      setSearchParams(prev => {
        const newParams = new URLSearchParams(prev);
        newParams.delete('recreate');
        return newParams;
      });
    }
  }, [searchParams, currentOrganization?.id]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (isDropdownOpen) {
        const target = event.target as Element;
        if (!target.closest('.relative')) {
          setIsDropdownOpen(false);
          setSearchTerm('');
        }
      }
    };

    if (isDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isDropdownOpen]);

  const loadAvailableWorkflows = async () => {
    if (!currentOrganization?.id || !user) {
      console.error('Cannot load workflow details without organization context');
      return;
    }
    
    setLoading(true);
    try {
      const token = await getToken({ template: 'withmail' });
      // Determine category based on active context
      const category = resolveCategoryForContext(activeContext);
      const response = await fetch(`/api/user/workflows/list?category=${category}&lang=${selectedLanguage}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'X-Organization-Id': currentOrganization.id
        },
      });

      if (response.ok) {
        const data = await response.json();
        const workflows = data.workflows || [];
        setAvailableWorkflows(workflows);

        // Priority for workflow selection:
        // 1. Workflow from URL parameter
        // 2. Last selected workflow from localStorage
        // 3. First workflow from list (fallback)

        const workflowIdFromUrl = searchParams.get('workflow');
        let workflowToLoad: any | null = null;

        if (workflowIdFromUrl) {
          // Try to find by either visual_id or workflow_id
          workflowToLoad = workflows.find(w => w.id === workflowIdFromUrl || w.workflow_id.toString() === workflowIdFromUrl);
        }

        if (!workflowToLoad && typeof window !== 'undefined' && currentOrganization?.id) {
          // 2. Try to restore from localStorage
          try {
            const lastWorkflowId = localStorage.getItem(getWorkflowStorageKey());
            if (lastWorkflowId) {
              workflowToLoad = workflows.find(w => w.id === lastWorkflowId || w.workflow_id.toString() === lastWorkflowId);
            }
          } catch (error) {
            console.warn('Failed to load last workflow from localStorage:', error);
          }
        }

        // 3. Fallback to first workflow if nothing else worked
        if (!workflowToLoad && workflows.length > 0 && !selectedWorkflow) {
          workflowToLoad = workflows[0];
        }

        // Load the selected workflow using its numeric workflow_id
        if (workflowToLoad) {
          await loadWorkflowDetails(workflowToLoad.workflow_id.toString());
        }
      } else {
        console.error('Failed to load available workflows');
        setAvailableWorkflows([]);
      }
    } catch (error) {
      console.error('Error loading available workflows:', error);
      setAvailableWorkflows([]);
    } finally {
      setLoading(false);
    }
  };

  const loadWorkflowDetails = async (workflowId: string) => {

    // Check cache first
    const cached = workflowDetailsCache.get(workflowId);
    if (cached) {
      setWorkflowAccessError(null); // Clear any previous error
      setSelectedWorkflow(cached);
      // Update URL
      setSearchParams(prev => {
        const newParams = new URLSearchParams(prev);
        newParams.set('workflow', workflowId);
        return newParams;
      });
      // Save to localStorage
      if (typeof window !== 'undefined' && currentOrganization?.id) {
        try {
          localStorage.setItem(getWorkflowStorageKey(), workflowId);
        } catch (error) {
          console.warn('Failed to save last workflow to localStorage:', error);
        }
      }
      return;
    }

    setLoadingDetails(true);
    setWorkflowAccessError(null); // Clear any previous error
    try {
      const token = await getToken({ template: 'withmail' });
      const response = await fetch(`/api/user/workflows/${workflowId}/details`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'X-Organization-Id': currentOrganization.id
        },
      });

      if (response.ok) {
        const workflowDetails = await response.json();

        // Cache the details
        setWorkflowDetailsCache(prev => {
          const newCache = new Map(prev);
          newCache.set(workflowId, workflowDetails);
          return newCache;
        });

        setSelectedWorkflow(workflowDetails);

        // Update URL
        setSearchParams(prev => {
          const newParams = new URLSearchParams(prev);
          newParams.set('workflow', workflowId);
          return newParams;
        });

        // Save to localStorage
        if (typeof window !== 'undefined' && currentOrganization?.id) {
          try {
            localStorage.setItem(getWorkflowStorageKey(), workflowId);
          } catch (error) {
            console.warn('Failed to save last workflow to localStorage:', error);
          }
        }
      } else if (response.status === 403) {
        // Access denied - workflow not available for this organization (e.g., NSFW restriction)
        const errorData = await response.json().catch(() => ({}));
        setWorkflowAccessError(errorData.error || 'Access denied to this workflow');
        setSelectedWorkflow(null);
      } else {
        console.error('Failed to load workflow details');
        setWorkflowAccessError('Failed to load workflow');
        setSelectedWorkflow(null);
      }
    } catch (error) {
      console.error('Error loading workflow details:', error);
      setWorkflowAccessError('Error loading workflow');
      setSelectedWorkflow(null);
    } finally {
      setLoadingDetails(false);
    }
  };

  const handleWorkflowChange = async (workflowId: string) => {
    if (workflowId) {
      // Don't update selectedWorkflow until we have full details
      // This prevents the widgets from disappearing temporarily
      await loadWorkflowDetails(workflowId);
    } else {
      setSelectedWorkflow(null);
      // Remove workflow parameter from URL
      setSearchParams(prev => {
        const newParams = new URLSearchParams(prev);
        newParams.delete('workflow');
        return newParams;
      });
    }
  };


  // Show prompt validation popup
  const showPromptRequiredPopup = useCallback(() => {
    alert(t.workflow?.promptRequired || 'Введите промпт для генерации');
  }, [t]);

  // Generation dispatch + async polling + active-generation restore live in a
  // shared hook so the Multi (parallel multi-model) section reuses the exact
  // same implementation (restore dedupes against existing slots by
  // asyncGenerationId, so a slot created on /multi is resumed, not duplicated).
  // Stable identity on purpose: an inline arrow here is a new function every
  // render, and it is a dep of the hook's dispatchGeneration useCallback — so it
  // would defeat that memoization, make handleGenerateClick unstable, and
  // re-register the Generate keydown listener on every single render.
  const handleLimitExceeded = useCallback(
    (type: 'organization' | 'user') => setLimitExceededModal({ isOpen: true, type }),
    []
  );
  const formatGenerationError = useCallback((error: string) =>
    formatModelSlotGenerationError(error, {
      invalidUrl: t.widgets?.slotsInputMedia?.errors?.invalidUrl || 'Invalid 3D model URL',
      unsupportedFormat: t.widgets?.slotsInputMedia?.unsupportedFormat || 'Unsupported format',
      fileTooLarge: t.widgets?.slotsInputMedia?.errors?.fileTooLarge || 'File too large',
    }), [t]);

  const { dispatchGeneration, restoreActiveGenerations } = useGenerationDispatch({
    selectedCollections,
    onLimitExceeded: handleLimitExceeded,
    formatGenerationError,
  });

  useEffect(() => registerDraftFinalConsumer(), [registerDraftFinalConsumer]);

  // Gallery → workbench hand-off. The parameter only carries a generation ID;
  // the workbench reloads the draft and asks for confirmation before any debit.
  // Wait for the organization like every recreate consumer: the workbench reads
  // the draft through an org-scoped request.
  // `loadWorkflowDetails` writes the URL through a `setSearchParams` captured
  // before this strip, so it can put the parameter back. Remember what this
  // mount already handled: a re-added value is stripped again but never
  // reopens the confirmation.
  const handledDraftHandoffs = useRef(new Set<string>());
  useEffect(() => {
    const raw = searchParams.get(DRAFT_FINAL_HANDOFF_PARAM);
    if (raw === null || !currentOrganization?.id) return;
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      next.delete(DRAFT_FINAL_HANDOFF_PARAM);
      return next;
    }, { replace: true });
    if (handledDraftHandoffs.current.has(raw)) return;
    handledDraftHandoffs.current.add(raw);
    const generationId = parseDraftFinalHandoffId(raw);
    if (generationId !== null) startDraftFinalHandoff(generationId);
  }, [searchParams, currentOrganization?.id, setSearchParams, startDraftFinalHandoff]);

  // Clearing context state does not synchronously change this effect's closure.
  // Claim each request before awaiting, including during StrictMode effect replay.
  const consumedDraftFinalRequests = useRef(new WeakSet<DraftFinalRequest>());
  const draftFinalScope = useRef({ mounted: true, organizationId: currentOrganization?.id, version: 0 });
  useEffect(() => {
    const scope = draftFinalScope.current;
    if (currentOrganization?.id != null && scope.organizationId !== currentOrganization.id) {
      scope.organizationId = currentOrganization.id;
      scope.version++;
    }
  }, [currentOrganization?.id]);
  useEffect(() => {
    draftFinalScope.current.mounted = true;
    return () => { draftFinalScope.current.mounted = false; };
  }, []);

  useEffect(() => {
    if (!draftFinalRequest || consumedDraftFinalRequests.current.has(draftFinalRequest)) return;
    consumedDraftFinalRequests.current.add(draftFinalRequest);
    clearDraftFinalRequest();
    if (!currentOrganization?.id) return;

    const request = draftFinalRequest;
    const scopeVersion = draftFinalScope.current.version;
    const isCurrent = () => draftFinalScope.current.mounted && draftFinalScope.current.version === scopeVersion;
    const startDraftFinal = async () => {
      let slotId: string | undefined;
      try {
        const token = await getToken({ template: 'withmail' });
        if (!isCurrent()) return;
        if (!token) throw new Error(t.draftFinal.loadError);
        const inputs = request.inputs;
        slotId = addGenerationSlot({
          status: 'pending',
          prompt: typeof inputs.prompt === 'string' ? inputs.prompt : '',
          settings: {
            imageSize: widgetState.imageSize,
            guidanceScale: inputs.guidance_scale || widgetState.guidanceScale,
            safetyTolerance: inputs.safety_tolerance || widgetState.safetyTolerance,
            numInferenceSteps: inputs.num_inference_steps || widgetState.numInferenceSteps,
          },
          isFavorite: false,
          workflowId: request.workflowId,
          noRefundOnContentPolicy: !!request.workflow.workflow.settings?.no_refund_on_content_policy,
          metadata: {
            userEmail: user?.primaryEmailAddress?.emailAddress,
            workflowName: request.workflowTitle,
            createdAt: new Date().toISOString(),
          },
        });
        addSentryBreadcrumb('generation', 'Draft final started', 'info', {
          workflowId: request.workflowId,
          sourceSlotId: request.sourceSlotId,
        });
        const result = await dispatchGeneration({
          workflow: request.workflow,
          generatePayload: inputs,
          requiredFiles: [],
          galleryUrls: request.galleryUrls,
          blobFileCount: 0,
          seedFields: [],
          index: 0,
          slotId,
          token,
        });
        if (!isCurrent()) return;
        if (!result.success) throw new Error(result.error || t.draftFinal.loadError);
        showSuccess(t.draftFinal.started);
      } catch (error) {
        if (!isCurrent()) return;
        const message = error instanceof Error ? error.message : t.draftFinal.loadError;
        if (slotId) updateGenerationSlot(slotId, { status: 'failed', error: message });
        showError(message);
      }
    };
    void startDraftFinal();
  }, [draftFinalRequest, clearDraftFinalRequest, currentOrganization?.id, getToken,
    addGenerationSlot, updateGenerationSlot, dispatchGeneration, widgetState, user, t, showError, showSuccess]);

  // Restore active generations on mount and when user/org changes
  useEffect(() => {
    if (user && currentOrganization) {
      restoreActiveGenerations();
    }
  }, [user, currentOrganization]); // Don't include restoreActiveGenerations to prevent loops


  // Handle keyboard shortcuts for Generate button
  const handleGenerateClick = useCallback(async (event?: React.MouseEvent) => {
    // Debug mode: Ctrl+Click (or Cmd+Click on Mac) 
    const isDebugMode = event && (event.ctrlKey || event.metaKey);
    
    if (isDebugMode) {
      console.log('🔍 DEBUG MODE: Collecting widget data without API call');
      
      if (!selectedWorkflow || !selectedWorkflow.widgets || !selectedWorkflow.widgets.length) {
        console.warn('No workflow selected or no widgets configured');
        return;
      }
      
      // Collect payload data from all widgets
      const activeWidgetRefs = Object.values(widgetRefs.current).filter(ref => ref.current);
      const payloadResult = collectWidgetPayloads(
        activeWidgetRefs,
        selectedLanguage,
        selectedWorkflow.workflow.input_files_logic || 'all_per_request'
      );
      
      // Log collected data
      console.log('📦 Collected Widget Payload:', {
        workflow: {
          id: selectedWorkflow.id,
          title: selectedWorkflow.workflow?.title || 'Loading...',
          category: selectedWorkflow.workflow?.category || 'Image'
        },
        payload: payloadResult.payload,
        validationErrors: payloadResult.validationErrors,
        requiredFiles: payloadResult.requiredFiles.map(f => ({
          name: f.name,
          size: f.size,
          type: f.type
        })),
        hasRequiredWidgetErrors: payloadResult.hasRequiredWidgetErrors,
        widgetState: {
          numImages: widgetState.numImages,
          imageSize: widgetState.imageSize,
          guidanceScale: widgetState.guidanceScale,
          safetyTolerance: widgetState.safetyTolerance,
          numInferenceSteps: widgetState.numInferenceSteps
        },
        selectedCollections: selectedCollections
      });
      
      // Show final payload that would be sent to API
      const generatePayload = payloadResult.payload;
      const finalPayload = {
        workflowId: selectedWorkflow.id,
        urlType: 'main',
        inputs: generatePayload,
        selectedCollections: selectedCollections.length > 0 ? selectedCollections : undefined
      };
      
      console.log('🚀 Final API Payload (not sent):', finalPayload);
      console.log('✅ Debug mode complete - no API call made');
      return;
    }
    
    // Prevent double clicks during queueing
    if (isQueueing) {
      console.warn('Already queueing, ignoring duplicate request');
      return;
    }

    if (!selectedWorkflow || !selectedWorkflow.widgets || !selectedWorkflow.widgets.length) {
      console.warn('No workflow selected or no widgets configured');
      return;
    }

    if (!currentOrganization?.id) {
      console.warn('No organization selected');
      return;
    }

    // Set queueing state immediately to prevent double clicks
    setIsQueueing(true);

    // Save workflow to usage history when user actually generates
    // Use workflow_id (numeric ID) for consistency with localStorage
    saveWorkflowToHistory(selectedWorkflow.workflow_id.toString());

    try {
      const token = await getToken({ template: 'withmail' });
      
      // Collect payload data from all widgets dynamically
      const activeWidgetRefs = Object.values(widgetRefs.current).filter(ref => ref.current);
      const payloadResult = collectWidgetPayloads(
        activeWidgetRefs,
        selectedLanguage,
        selectedWorkflow.workflow.input_files_logic || 'all_per_request'
      );
      // Check for validation errors
      if (payloadResult.hasRequiredWidgetErrors) {
        const errorMessages = payloadResult.validationErrors.map(e => e.message).join('\n');
        setValidationErrorMessage(errorMessages);
        return;
      }
      const seedAdvances = captureWidgetSeedAdvances(activeWidgetRefs);
      
      // Use collected payload
      const generatePayload = payloadResult.payload;

      // Create generation slots based on expected API requests
      const slotIds: string[] = [];
      // Check gen_count first (for backwards compatibility), then num_images, then default to 1
      // Note: We don't use widgetState.numImages here because it's global state that persists across workflows
      // If a workflow has a "Generation Settings" widget, it will set gen_count or num_images in the payload
      const numImages = generatePayload.gen_count || generatePayload.num_images || 1;
      
      console.log('🔢 Calculating slots:', {
        generatePayloadGenCount: generatePayload.gen_count,
        generatePayloadNumImages: generatePayload.num_images,
        widgetStateNumImages: widgetState.numImages,
        finalNumImages: numImages,
        requiredFilesCount: payloadResult.requiredFiles.length
      });
      
      // Calculate actual number of slots needed based on files and workflow logic
      // Include both uploaded files and gallery items in the count
      const expectedSlots = calculateExpectedSlots(
        selectedWorkflow.workflow,
        payloadResult.requiredFiles,
        numImages,
        payloadResult.galleryUrls?.length || 0
      );
      
      console.log('🎯 Expected slots:', expectedSlots);
      
      // Update queued count to show in button
      setQueuedCount(expectedSlots);
      
      for (let i = 0; i < expectedSlots; i++) {
        const slotId = addGenerationSlot({
          status: 'pending',
          prompt: generatePayload.prompt || '',
          settings: {
            imageSize: widgetState.imageSize,
            guidanceScale: generatePayload.guidance_scale || widgetState.guidanceScale,
            safetyTolerance: generatePayload.safety_tolerance || widgetState.safetyTolerance,
            numInferenceSteps: generatePayload.num_inference_steps || widgetState.numInferenceSteps
          },
          isFavorite: false,
          noRefundOnContentPolicy: !!selectedWorkflow?.workflow?.settings?.no_refund_on_content_policy,
          // Numeric internal workflow id (`id` is visual_id for global workflows) —
          // keeps slot.workflowId in one id space with the Multi page.
          workflowId: selectedWorkflow?.workflow_id ?? selectedWorkflow?.id,
          metadata: {
            userEmail: user?.primaryEmailAddress?.emailAddress,
            workflowName: selectedWorkflow?.workflow?.title,
            createdAt: new Date().toISOString()
          }
        });
        slotIds.push(slotId);
      }
      addSentryBreadcrumb('generation', 'Started generation', 'info', {
        workflowId: selectedWorkflow.id,
        workflowTitle: selectedWorkflow.workflow?.title,
        numSlots: expectedSlots,
        isPipelineMode: selectedWorkflow.workflow?.settings?.pipeline_mode === 'pipeline' ||
          (Array.isArray(selectedWorkflow.workflow?.settings?.steps) && selectedWorkflow.workflow.settings.steps.length > 0),
      });

      const fanOutInputs = await prepareFanOutInputs({
        expectedSlots,
        canShareAcrossRequests:
          (selectedWorkflow.workflow.input_files_logic || 'all_per_request') === 'all_per_request',
        requiredFiles: payloadResult.requiredFiles,
      }, files => uploadGenerationInputs(files, token, currentOrganization.id));

      if (fanOutInputs.preUploadError) {
        console.warn('Input pre-upload failed; falling back to per-request uploads', fanOutInputs.preUploadError);
        addSentryBreadcrumb('generation', 'Input pre-upload failed; using request uploads', 'warning', {
          workflowId: selectedWorkflow.id,
          numSlots: expectedSlots,
          fileCount: payloadResult.requiredFiles.length,
        });
      }

      // A pre-upload can consume most of a short-lived Clerk token's lifetime.
      // Refresh after either success or fallback so every execute request starts
      // with current credentials.
      const dispatchToken = fanOutInputs.preUploadedUrls || fanOutInputs.preUploadError
        ? await refreshGenerationToken(() => getToken({ template: 'withmail' }), token)
        : token;

      // Start generation for each slot — each dispatched via the shared helper
      const promises = slotIds.map((slotId, i) => dispatchGeneration({
        workflow: selectedWorkflow,
        generatePayload,
        requiredFiles: fanOutInputs.requiredFiles,
        preUploadedUrls: fanOutInputs.preUploadedUrls,
        galleryUrls: payloadResult.galleryUrls || [],
        blobFileCount: payloadResult.blobFileCount,
        seedFields: payloadResult.seedFields,
        index: i,
        slotId,
        token: dispatchToken,
      }));

      // Payload collection and every dispatch call above already captured the
      // current base seed. Advance only now so the field describes the next
      // launch without changing any request in this batch.
      advanceWidgetSeeds(seedAdvances);

      // Dispatch has started for every slot. Keep the button locked through the
      // potentially slow pre-upload, but allow a new batch while these requests run.
      setIsQueueing(false);
      setQueuedCount(0);

      // Wait for all generations to complete
      const results = await Promise.allSettled(promises);
      
      // Generation completed
      
      const successCount = results.filter(r => r.status === 'fulfilled' && r.value.success).length;
      const failedCount = results.length - successCount;
      
      if (failedCount > 0) {
        console.warn(`Generation completed with ${failedCount} failures out of ${results.length} total`);
      }

      addSentryBreadcrumb('generation', 'Batch generation completed', failedCount > 0 ? 'warning' : 'info', {
        workflowId: selectedWorkflow.id,
        totalSlots: results.length,
        successCount,
        failedCount,
      });

      // Refresh token balance after generation
      refreshUsage();

    } catch (error) {
      console.error('Generation error:', error);
    } finally {
      setIsQueueing(false);
      setQueuedCount(0);
    }
  }, [selectedWorkflow, widgetState, currentOrganization, getToken, showPromptRequiredPopup, addGenerationSlot, updateGenerationSlot, widgetRefs, selectedCollections, isQueueing, dispatchGeneration, selectedLanguage]);

  // Add keyboard event listener for Generate shortcut
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Check for Cmd+Enter (Mac) or Ctrl+Enter (Windows/Linux)
      if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) {
        event.preventDefault();
        handleGenerateClick();
      }
    };

    // Only add listener if there's a selected workflow with widgets
    if (selectedWorkflow && selectedWorkflow.widgets && selectedWorkflow.widgets.length > 0) {
      window.addEventListener('keydown', handleKeyDown);
      return () => window.removeEventListener('keydown', handleKeyDown);
    }
  }, [selectedWorkflow, handleGenerateClick]);


  // Render specific widget based on type (only small widgets in sidebar)
  const renderWidget = (widget: SidebarWidgetItem) => {
    // Handle embedded dynamic widget
    if ('isEmbedded' in widget && widget.isEmbedded) {
      const embeddedWidgetId = `embedded_widget_${selectedWorkflow?.workflow_id}`;
      return (
        <EmbeddedDynamicWidgetRenderer
          workflowId={selectedWorkflow?.workflow_id || 0}
          config={widget.embeddedConfig}
          selectedLanguage={selectedLanguage}
          widgetRef={getWidgetRef(embeddedWidgetId)}
        />
      );
    }

    // Regular workflow widget
    const regularWidget = widget as WorkflowWidget;
    const widgetTypeId = regularWidget.widget_type_id;
    const widgetTypeName = regularWidget.widget_type.name;


    if (widgetTypeName === 'Collections Selector') {
      return (
        <CollectionsSelectorWidget
          language={selectedLanguage}
          selectedCollections={selectedCollections}
          onCollectionsChange={handleCollectionsChange}
          skipAutoSelectDefault={collectionsExplicitlySet}
        />
      );
    }

    if (widgetTypeName === 'Input Text') {
      let config = {};
      // First try to get config from this widget
      if (regularWidget.config_data) {
        try {
          config = JSON.parse(regularWidget.config_data);
        } catch (e) {
          console.warn('Failed to parse Input Text config:', e);
        }
      }
      // If no config, try to find it from the big version of Input Text widget
      if (Object.keys(config).length === 0 && selectedWorkflow?.widgets) {
        const bigInputTextWidget = selectedWorkflow.widgets.find(
          w => w.widget_type.name === 'Input Text' &&
               w.widget_type.display_type === 'big' &&
               w.config_data
        );
        if (bigInputTextWidget?.config_data) {
          try {
            config = JSON.parse(bigInputTextWidget.config_data);
          } catch (e) {
            console.warn('Failed to parse big Input Text config:', e);
          }
        }
      }
      const widgetId = 'input_text_small';
      return (
        <InputTextSmallWidget
          language={selectedLanguage}
          prompt={widgetState.prompt || ''}
          onPromptChange={(prompt) => updateWidgetState({ prompt })}
          config={config}
          widgetRef={getWidgetRef(widgetId)}
        />
      );
    }

    // Handle Slots Input Media Small widget
    if (widgetTypeName === 'Slots Input Media Small') {
      let config = {};
      if (regularWidget.config_data) {
        try {
          config = JSON.parse(regularWidget.config_data);
        } catch (e) {
          console.warn('Failed to parse Slots Input Media Small config:', e);
        }
      }
      // If no config, try to find it from the big version of Slots Input Media widget
      if (Object.keys(config).length === 0 && selectedWorkflow?.widgets) {
        const bigSlotsWidget = selectedWorkflow.widgets.find(
          w => w.widget_type.name === 'Slots Input Media' &&
               w.widget_type.display_type === 'big' &&
               w.config_data
        );
        if (bigSlotsWidget?.config_data) {
          try {
            config = JSON.parse(bigSlotsWidget.config_data);
          } catch (e) {
            console.warn('Failed to parse big Slots Input Media config:', e);
          }
        }
      }
      const widgetId = 'slots_input_media_small';
      return (
        <SlotsInputMediaSmallWidget
          language={selectedLanguage}
          config={config}
          widgetRef={getWidgetRef(widgetId)}
          organizationId={currentOrganization?.id ?? null}
        />
      );
    }

    // Check if this is a dynamic widget
    if (regularWidget.widget_type.is_dynamic) {
      const widgetId = `widget_${widgetTypeId}`;
      return (
        <DynamicWidgetRenderer
          widget={regularWidget}
          selectedLanguage={selectedLanguage}
          widgetId={widgetId}
          widgetRef={getWidgetRef(widgetId)}
        />
      );
    }

    // Fallback to placeholder
    return (
      <div className="text-center py-2 text-xs text-gray-500 bg-gray-900/30 rounded border border-gray-700/30">
        {t.workflow?.widgetComingSoon || 'Widget configuration coming soon'}
      </div>
    );
  };

  // Get small widgets for sidebar (including embedded dynamic widget)
  const getSmallWidgets = (): SidebarWidgetItem[] => {
    if (!selectedWorkflow) {
      return [];
    }

    const result: SidebarWidgetItem[] = [];

    // Add regular small widgets
    if (selectedWorkflow.widgets) {
      const regularSmallWidgets = selectedWorkflow.widgets
        .filter(widget => widget.widget_type.display_type === 'small');
      result.push(...regularSmallWidgets);
    }

    // Add embedded dynamic widget if exists
    if (selectedWorkflow.dynamic_widget?.name &&
        selectedWorkflow.dynamic_widget?.fields?.length > 0) {
      const embeddedItem: EmbeddedWidgetItem = {
        id: `embedded_dynamic_${selectedWorkflow.workflow_id}`,
        display_order: selectedWorkflow.dynamic_widget.display_order,
        isEmbedded: true,
        embeddedConfig: selectedWorkflow.dynamic_widget
      };
      result.push(embeddedItem);
    }

    return result.sort((a, b) => a.display_order - b.display_order);
  };

  // Get big widgets for central area (exported for use in BigWidgetsArea)
  const getBigWidgets = () => {
    if (!selectedWorkflow || !selectedWorkflow.widgets) {
      return [];
    }
    const bigWidgets = selectedWorkflow.widgets
      .filter(widget => widget.widget_type.display_type === 'big')
      .sort((a, b) => a.display_order - b.display_order);
    return bigWidgets;
  };

  // Make selectedWorkflow and getBigWidgets available to parent
  React.useEffect(() => {
    if (window.widgetContextBridge) {
      // Only update if we have a workflow with full details (including widgets)
      // This prevents overwriting full details with lightweight data
      if (!selectedWorkflow || (selectedWorkflow && selectedWorkflow.widgets)) {
        window.widgetContextBridge.selectedWorkflow = selectedWorkflow;
        window.widgetContextBridge.lastUpdate = Date.now(); // Add timestamp for change detection
      }
      window.widgetContextBridge.getBigWidgets = getBigWidgets;
      window.widgetContextBridge.renderWidget = renderWidget;
      window.widgetContextBridge.getWidgetRef = getWidgetRef;

      // NEW: Add collectAllPayloads for use in Translate/Improve buttons
      window.widgetContextBridge.collectAllPayloads = () => {
        const activeWidgetRefs = Object.values(widgetRefs.current).filter(ref => ref.current);
        return collectWidgetPayloads(
          activeWidgetRefs,
          selectedLanguage,
          selectedWorkflow?.workflow?.input_files_logic || 'all_per_request'
        );
      };

      // Notify BigWidgetsArea about updates
      if (window.widgetContextBridge.updateBigWidgetsArea) {
        window.widgetContextBridge.updateBigWidgetsArea();
      }
    }
  }, [selectedWorkflow, selectedLanguage]);

  // Payload preview "All users" tab: global admins only, scoped to the selected workflow.
  // Keyed on primitives: the loader identity changes only with the workflow, and
  // DataPopup resets its All users tab whenever the loader changes.
  const adminHistoryWorkflow = resolveAdminHistoryWorkflow(userAccess?.isAdmin, selectedWorkflow);
  const adminHistoryWorkflowId = adminHistoryWorkflow?.id;
  const adminHistoryWorkflowTitle = adminHistoryWorkflow?.title;
  const adminHistoryLoader = useMemo(
    () => (adminHistoryWorkflow ? buildAdminHistoryLoader(getToken, adminHistoryWorkflow) : undefined),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [getToken, adminHistoryWorkflowId, adminHistoryWorkflowTitle]
  );

  if (!currentOrganization) {
    return (
      <div className="h-full flex flex-col">
        <TokenBalanceCard
          selectedLanguage={selectedLanguage}
          isRefreshing={isRefreshingBalance}
        />

        <div className="flex flex-1 items-center justify-center p-6">
          <div className="text-center">
            <h3 className="text-lg font-semibold text-white mb-2">
              {t.workflow?.selectOrganization || 'Select Organization'}
            </h3>
            <p className="text-sm text-gray-400 max-w-48">
              {t.workflow?.selectOrganizationDesc || 'Select an organization to access workflows'}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      <TokenBalanceCard
        selectedLanguage={selectedLanguage}
        isRefreshing={isRefreshingBalance}
      />

      {/* Workflow Selector */}
      <div className="p-4 border-b border-gray-700/50">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <label className="text-xs font-medium text-gray-300">
              {t.workflow?.workflowLabel || 'Workflow'}
            </label>
            {/* Edit button - only for global admins */}
            {userAccess?.isAdmin && selectedWorkflow && (
              <button
                type="button"
                onClick={() => workflowEditor.open(selectedWorkflow.workflow_id)}
                disabled={workflowEditor.loading}
                className="p-1 hover:bg-gray-700/50 rounded transition-colors disabled:opacity-50"
                title={t.workflow?.editWorkflow || 'Edit workflow'}
              >
                {workflowEditor.loading ? (
                  <div className="animate-spin rounded-full h-3 w-3 border border-gray-400 border-t-transparent"></div>
                ) : (
                  <Icon name={ICONS.EDIT} size={14} className="text-gray-400 hover:text-white" />
                )}
              </button>
            )}
            {/* Preview payload button - only for global admins */}
            {userAccess?.isAdmin && selectedWorkflow && (
              <button
                type="button"
                onClick={() => setShowPayloadPreview(true)}
                className="p-1 hover:bg-gray-700/50 rounded transition-colors"
                title={t.workflow?.previewPayload || 'Preview request data'}
              >
                <Icon name={ICONS.EYE} size={14} className="text-gray-400 hover:text-white" />
              </button>
            )}
          </div>
          <Tooltip content={t.workflow?.selectWorkflowDesc || 'Choose workflow and configure widgets'}>
            <div className="w-4 h-4 bg-gray-700/50 border border-gray-600 rounded-full flex items-center justify-center cursor-help hover:bg-gray-600/50 transition-colors duration-200">
              <span className="text-xs text-gray-300 font-medium">?</span>
            </div>
          </Tooltip>
        </div>
        
        {loading ? (
          <div className="flex items-center justify-center py-3">
            <div className="animate-spin rounded-full h-5 w-5 border-2 border-purple-500 border-t-transparent"></div>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => {
              if (onOpenTileSelector) {
                onOpenTileSelector(
                  availableWorkflows,
                  selectedWorkflow?.id || null,
                  (workflowId) => {
                    const workflow = availableWorkflows.find(w => w.id === workflowId);
                    if (workflow) {
                      handleWorkflowChange(workflow.workflow_id.toString());
                    }
                  }
                );
              }
            }}
            className="w-full px-3 py-2 text-sm text-left rounded-lg bg-gradient-to-r from-purple-900/80 to-indigo-900/80 border-2 border-purple-400/60 text-white hover:border-purple-300/80 focus:border-purple-300/80 transition-all duration-200 backdrop-blur-sm focus:outline-none focus:ring-2 focus:ring-purple-500/30 flex items-center justify-between"
          >
            <span className={selectedWorkflow ? "text-white" : "text-gray-400"}>
              {selectedWorkflow ? (selectedWorkflow.workflow?.title || selectedWorkflow.title || 'Loading...') : (t.workflow?.selectWorkflowPlaceholder || 'Choose workflow...')}
            </span>
            <div className="flex-shrink-0">
              <svg className="w-5 h-5 text-gray-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
            </div>
          </button>
        )}
      </div>

      {/* Selected Workflow Info */}
      {selectedWorkflow && selectedWorkflow.workflow && !workflowAccessError && (
        <div className="p-4 border-b border-gray-700/50">
          <p className="text-xs text-gray-400">
            {selectedWorkflow.workflow.description_translations?.[selectedLanguage] ||
             selectedWorkflow.workflow.description_translations?.['en'] ||
             'AI image generation workflow'}
          </p>
        </div>
      )}

      {/* Loading Details Indicator */}
      {loadingDetails && (
        <div className="p-4 border-b border-gray-700/50">
          <div className="flex items-center justify-center gap-2">
            <div className="animate-spin rounded-full h-4 w-4 border-2 border-purple-500 border-t-transparent"></div>
            <span className="text-sm text-gray-400">{t.workflow?.loadingDetails || 'Loading workflow details...'}</span>
          </div>
        </div>
      )}

      {/* Small Widgets Section */}
      <div className="flex-1 overflow-y-auto">
        {workflowAccessError ? (
          <div className="p-4 text-center">
            <div className="text-4xl mb-3">🔒</div>
            <p className="text-sm text-red-400 mb-2 font-medium">
              {t.workflow?.accessDenied || 'Access Denied'}
            </p>
            <p className="text-xs text-gray-400">
              {workflowAccessError}
            </p>
            <p className="text-xs text-gray-500 mt-2">
              {t.workflow?.accessDeniedHint || 'This workflow is not available for your organization.'}
            </p>
          </div>
        ) : selectedWorkflow && !loadingDetails ? (
          getSmallWidgets().length === 0 ? (
            <div className="p-4 text-center">
              <div className="text-4xl mb-2">🧩</div>
              <p className="text-sm text-gray-400 mb-1">
                {t.workflow?.noSmallWidgets || 'No Small Widgets'}
              </p>
              <p className="text-xs text-gray-500">
                {t.workflow?.noSmallWidgetsDesc || 'This workflow has no small widgets for the sidebar.'}
              </p>
            </div>
          ) : (
            <div className="p-4 space-y-3">
              {getSmallWidgets().map((widget, index) => (
                <div key={widget.id} className="mb-4">
                  {renderWidget(widget)}
                </div>
              ))}
            </div>
          )
        ) : (
          <div className="p-4 text-center">
            <p className="text-sm text-gray-400">
              {t.workflow?.selectWorkflow || 'Select a workflow to get started'}
            </p>
          </div>
        )}
      </div>

      {/* Action Button - Show if workflow has any widgets */}
      {selectedWorkflow && selectedWorkflow.widgets && selectedWorkflow.widgets.length > 0 && !loadingDetails && (() => {
        const actionConfig = getActionButtonConfig(selectedWorkflow.workflow?.action_button_type || 'generate', t);
        const tokenCost = calculateTokenCost();
        const isLoading = tokenCost === -1;
        const requiresImages = tokenCost === -2;
        const missingParams = tokenCost === -3;
        const requiresAudio = tokenCost === -4;
        const requiresVideo = tokenCost === -5;
        const requiresAudioOrVideo = tokenCost === -6;
        const requiresPrompt = tokenCost === -7;
        const requiresText = tokenCost === -8;
        const requiresAnyInput = tokenCost === -9;
        const hasPricingError = tokenCost === null;
        const isGenerateDisabled = isQueueing || hasPricingError || requiresImages || requiresAudio || requiresVideo || requiresAudioOrVideo || requiresPrompt || requiresText || requiresAnyInput;
        const discountInfo = discountInfoRef.current;

        // Renders cost number with discount tooltip or plain
        const renderCostNumber = (cost: number) => {
          if (discountInfo) {
            return (
              <Tooltip content={
                <div>{t.workflow?.priceWithoutDiscount || 'Price without discount'}: {discountInfo.fullPrice}</div>
              }>
                <span className="border-b border-dashed border-gray-400 cursor-help">{cost}</span>
              </Tooltip>
            );
          }
          return <>{cost}</>;
        };

        return (
          <div className="p-4 border-t border-gray-700/50">
            {/* Content Policy No-Refund Notice */}
            {selectedWorkflow?.workflow?.settings?.no_refund_on_content_policy && (
              <div className="mb-3 flex items-start gap-2 bg-blue-900/20 border border-blue-500/30 rounded-lg px-3 py-2">
                <Icon name={ICONS.INFO} size={16} className="text-blue-400 mt-0.5 flex-shrink-0" />
                <span className="text-xs text-blue-300">
                  {t.workflow?.contentPolicyChargeNotice || 'When the request is deemed to be in violation of content policy, the generation will still be charged.'}
                </span>
              </div>
            )}
            {/* Token Cost Display or Error */}
            <div className="mb-3 text-center">
              {hasPricingError ? (
                <div className="text-sm text-red-400 bg-red-900/20 border border-red-500/30 rounded px-3 py-2">
                  ⚠️ {t.workflow?.invalidPricingFormula || 'Invalid pricing formula. Contact administrator.'}
                </div>
              ) : requiresImages ? (
                <span className="text-sm text-yellow-400">
                  {t.workflow?.requiresImages || 'Add images to calculate cost'}
                </span>
              ) : requiresAudio ? (
                <span className="text-sm text-yellow-400">
                  {t.workflow?.requiresAudio || 'Add audio to calculate cost'}
                </span>
              ) : requiresVideo ? (
                <span className="text-sm text-yellow-400">
                  {t.workflow?.requiresVideo || 'Add video to calculate cost'}
                </span>
              ) : requiresAudioOrVideo ? (
                <span className="text-sm text-yellow-400">
                  {t.workflow?.requiresAudioOrVideo || 'Add audio or video to calculate cost'}
                </span>
              ) : requiresPrompt ? (
                <span className="text-sm text-yellow-400">
                  {t.workflow?.requiresPrompt || 'Enter prompt to calculate cost'}
                </span>
              ) : requiresText ? (
                <span className="text-sm text-yellow-400">
                  {t.workflow?.requiresText || 'Enter text to calculate cost'}
                </span>
              ) : requiresAnyInput ? (
                <span className="text-sm text-yellow-400">
                  {(() => {
                    const pf = selectedWorkflow?.workflow?.pricing_formula || '';
                    const parts: string[] = [];
                    if (MEDIA_DURATION_FORMULA_PATTERNS.optionalAudio.test(pf)) parts.push(t.workflow?.inputAudio || 'audio');
                    if (MEDIA_DURATION_FORMULA_PATTERNS.optionalVideo.test(pf)) parts.push(t.workflow?.inputVideo || 'video');
                    if (/\bprompt_length_ceil_100_may_zero\b/.test(pf)) parts.push(t.workflow?.inputPrompt || 'prompt');
                    if (/\btext_length_ceil_100_may_zero\b/.test(pf)) parts.push(t.workflow?.inputText || 'text');
                    const joined = parts.join(` ${t.workflow?.orSeparator || 'or'} `);
                    return `${t.workflow?.requiresAnyInput || 'Provide'} ${joined}`;
                  })()}
                </span>
              ) : missingParams || isLoading ? (
                <span className="text-sm text-gray-400">
                  <span className="opacity-50">{t.workflow?.calculating || 'Calculating...'}</span>
                </span>
              ) : typeof tokenCost === 'object' && tokenCost !== null ? (
                tokenCost.isMultiGeneration ? (
                  // Multi-generation (num_images > 1)
                  <span className="text-sm text-white flex items-center justify-center gap-1.5">
                    <Icon name={ICONS.ZAPS} size={14} className="text-yellow-400" />
                    {renderCostNumber(tokenCost.totalCost)} {t.workflow?.tokensForGenerations || 'tokens for generations'}
                  </span>
                ) : (
                  // Multi-file cost display
                  <div className="text-sm">
                    <div className="text-white font-medium flex items-center justify-center gap-1.5">
                      <Icon name={ICONS.ZAPS} size={14} className="text-yellow-400" />
                      {renderCostNumber(tokenCost.totalCost)} {t.workflow?.tokensForGenerations || 'tokens for generations'}
                    </div>
                    <div className="text-xs text-gray-400 mt-1">
                      {tokenCost.requestCount} {t.workflow?.requests || 'requests'}: {tokenCost.minCost === tokenCost.maxCost ? tokenCost.minCost : `${tokenCost.minCost}-${tokenCost.maxCost}`} {t.workflow?.tokensEach || 'tokens each'}
                    </div>
                  </div>
                )
              ) : (
                // Single cost display (1 generation)
                <span className="text-sm text-white flex items-center justify-center gap-1.5">
                  <Icon name={ICONS.ZAPS} size={14} className="text-yellow-400" />
                  {renderCostNumber(tokenCost as number)} {t.workflow?.tokensForGeneration || 'tokens for generation'}
                </span>
              )}
            </div>

            <button
              className={`w-full py-3 text-white font-medium rounded-lg transition-all duration-200 shadow-lg flex items-center justify-between ${
                isGenerateDisabled
                  ? 'bg-gray-600 cursor-not-allowed'
                  : `bg-gradient-to-r ${actionConfig.gradientFrom} ${actionConfig.gradientTo} ${actionConfig.hoverFrom} ${actionConfig.hoverTo} hover:shadow-xl`
              }`}
              onClick={(e) => handleGenerateClick(e)}
              disabled={isGenerateDisabled}
            >
              <div className="flex items-center justify-center gap-2 flex-1">
                {isQueueing ? (
                  <>
                    <div className="animate-spin w-5 h-5 border-2 border-white border-t-transparent rounded-full"></div>
                    <span>
                      {t.workflow?.queued?.replace('{count}', queuedCount.toString()) || `${queuedCount} Queued`}
                    </span>
                  </>
                ) : (
                  <>
                    <Icon name={ICONS[actionConfig.icon] || ICONS.IMAGE} size={20} className="text-current" />
                    <span>{actionConfig.text}</span>
                  </>
                )}
              </div>
              {!isQueueing && (navigator.platform.toLowerCase().includes('mac') || navigator.platform.toLowerCase().includes('darwin')) && (
                <div className="bg-gray-800/70 px-3 py-1.5 rounded text-base font-mono mr-2">
                  {t.workflow?.keyboardShortcutMac || '⌘↩'}
                </div>
              )}
              {!isQueueing && (navigator.platform.toLowerCase().includes('win') || navigator.userAgent.toLowerCase().includes('windows')) && (
                <div className="bg-gray-800/70 px-3 py-1.5 rounded text-base font-mono mr-2">
                  {t.workflow?.keyboardShortcutWindows || 'Ctrl+↩'}
                </div>
              )}
            </button>
          </div>
        );
      })()}

      <NotificationModal {...notification} onClose={hideNotification} />

      {/* Limit Exceeded Modal */}
      {limitExceededModal.isOpen && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50" onClick={() => setLimitExceededModal({ isOpen: false, type: null })}>
          <div className="bg-gray-900 border-2 border-red-500/50 rounded-lg p-6 max-w-md w-full mx-4" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-3 mb-4">
              <div className="text-3xl">⚠️</div>
              <h2 className="text-xl font-bold text-red-400">
                {t.workflow?.limitExceededTitle || 'Token Limit Exceeded'}
              </h2>
            </div>

            <p className="text-white mb-6">
              {t.workflow?.organizationLimitExceeded || 'Organization token limit has been exceeded. Please contact your administrator.'}
            </p>

            <button
              onClick={() => setLimitExceededModal({ isOpen: false, type: null })}
              className="w-full bg-red-600 hover:bg-red-700 px-4 py-2 rounded-lg text-white font-medium transition-colors"
            >
              {t.close || 'Close'}
            </button>
          </div>
        </div>
      )}

      {/* Edit Workflow Modal (for global admins) */}
      {workflowEditor.renderModal(selectedLanguage)}

      {/* Payload Preview Modal (for global admins) */}
      <DataPopup
        isOpen={showPayloadPreview}
        title={t.workflow?.previewPayloadTitle || 'API Request Data Preview'}
        data={(() => {
          const activeWidgetRefs = Object.values(widgetRefs.current).filter(ref => ref.current);
          const payloadResult = collectWidgetPayloads(
            activeWidgetRefs,
            selectedLanguage,
            selectedWorkflow?.workflow?.input_files_logic || 'all_per_request'
          );

          // Categorize files by media type to show what will be sent
          const categorizedFiles = categorizeFilesForPreview(payloadResult.requiredFiles);

          // Categorize gallery items by media type
          const { images: galleryImages, videos: galleryVideos, audios: galleryAudios } =
            categorizeGalleryByMediaType(payloadResult.galleryUrls);

          return {
            workflowId: selectedWorkflow?.workflow_id,
            urlType: 'main',
            inputs: {
              ...payloadResult.payload,
              // Combine file uploads and gallery items for each media type
              ...((categorizedFiles.images.length > 0 || galleryImages.length > 0) && {
                image_urls: [
                  ...categorizedFiles.images.map(f => `<will upload: ${f.name}>`),
                  ...galleryImages.map(g => g.url)
                ]
              }),
              ...((categorizedFiles.videos.length > 0 || galleryVideos.length > 0) && {
                video_urls: [
                  ...categorizedFiles.videos.map(f => `<will upload: ${f.name}>`),
                  ...galleryVideos.map(g => g.url)
                ]
              }),
              ...((categorizedFiles.audios.length > 0 || galleryAudios.length > 0) && {
                audio_urls: [
                  ...categorizedFiles.audios.map(f => `<will upload: ${f.name}>`),
                  ...galleryAudios.map(g => g.url)
                ]
              }),
            },
            filesCount: payloadResult.requiredFiles.length + payloadResult.galleryUrls.length,
            validationErrors: payloadResult.validationErrors.length > 0 ? payloadResult.validationErrors : undefined
          };
        })()}
        history={getSentRequestHistory()}
        adminHistoryLoader={adminHistoryLoader}
        tabLabels={{
          current: t.orgHistory?.dataPopupCurrent,
          history: t.orgHistory?.dataPopupHistory,
          tracing: t.orgHistory?.dataPopupTracing,
          copy: t.orgHistory?.dataPopupCopy,
          copied: t.orgHistory?.dataPopupCopied,
          noHistory: t.orgHistory?.dataPopupNoHistory,
          allUsers: t.orgHistory?.dataPopupAllUsers,
          allUsersEmpty: t.orgHistory?.dataPopupAllUsersEmpty,
          allUsersError: t.orgHistory?.dataPopupAllUsersError,
          allUsersLoading: t.orgHistory?.dataPopupAllUsersLoading,
        }}
        onClose={() => setShowPayloadPreview(false)}
      />

      {/* Validation Error Notification - rendered via portal for proper centering */}
      {validationErrorMessage !== null && createPortal(
        <NotificationModal
          isOpen={true}
          type="warning"
          title={t.workflow?.validationError || 'Validation Error'}
          message={validationErrorMessage}
          duration={5000}
          onClose={() => setValidationErrorMessage(null)}
        />,
        document.body
      )}
    </div>
  );
}
